import React, { useState, useEffect, useCallback, useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AcademicYearSelectFilter from "../components/common/AcademicYearSelectFilter";
import TermSelectFilter from "../components/common/TermSelectFilter";
import VoteHeadSelectFilter from "../components/common/VoteHeadSelectFilter";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import FeeStructureCard from "../components/feeStructures/FeeStructureCard";
import CreateFeeStructureModal from "../components/feeStructures/CreateFeeStructureModal";
import UpdateFeeStructureModal from "../components/feeStructures/UpdateFeeStructureModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getFeeStructures,
  deleteFeeStructure,
  resetFeeStructureServerStatus,
} from "../redux/actions/feeStructureActions";
import { getAcademicYears } from "../redux/actions/academicYearActions";
import { getTerms } from "../redux/actions/termActions";
import { getGradeLevels } from "../redux/actions/gradeLevelActions";
import { getVoteHeads } from "../redux/actions/voteHeadActions";
import { FeeStructureResponseDto } from "../shared/dtos/feeStructure/FeeStructureResponseDto";

const FeeStructuresPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- REDUX SELECTORS ---
  const {
    items: feeStructures,
    loadingFetchAll: loadingFeeStructures,
    errorFetchAll: errorLoadingFeeStructures,
    serverStatus,
    loadingCreate: loadingFeeStructureCreate,
    errorCreate: errorCreatingFeeStructure,
    loadingUpdate: loadingFeeStructureUpdate,
    errorUpdate: errorUpdatingFeeStructure,
    loadingDelete: loadingFeeStructureDelete,
  } = useAppSelector((state) => state.feeStructure);

  const { userInfo } = useAppSelector((state) => state.auth);

  // Lookups
  const { items: academicYears } = useAppSelector(
    (state) => state.academicYear,
  );
  const { items: terms } = useAppSelector((state) => state.term);
  const { items: gradeLevels } = useAppSelector((state) => state.gradeLevel);
  const { items: voteHeads } = useAppSelector((state) => state.voteHead);

  // Local Filter States (IDs)
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedYearId, setSelectedYearId] = useState<string>("");
  const [selectedTermId, setSelectedTermId] = useState<string>("");
  const [selectedGradeId, setSelectedGradeId] = useState<string>("");
  const [selectedVoteHeadId, setSelectedVoteHeadId] = useState<string>("");

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedFeeStructure, setSelectedFeeStructure] =
    useState<FeeStructureResponseDto | null>(null);

  const canCreate = hasPermission("fee_structure:create");
  const canUpdate = hasPermission("fee_structure:update");
  const canDelete = hasPermission("fee_structure:delete");

  useEffect(() => {
    if (!academicYears.length) dispatch(getAcademicYears());
    if (!terms.length) dispatch(getTerms());
    if (!gradeLevels.length) dispatch(getGradeLevels());
    if (!voteHeads.length) dispatch(getVoteHeads());
  }, [
    dispatch,
    academicYears.length,
    terms.length,
    gradeLevels.length,
    voteHeads.length,
  ]);

  const fetchFeeStructuresList = useCallback((): void => {
    dispatch(
      getFeeStructures({
        branchId: selectedBranchId || undefined,
        academicYearId: selectedYearId || undefined,
        termId: selectedTermId || undefined,
        gradeLevelId: selectedGradeId || undefined,
        voteHeadId: selectedVoteHeadId || undefined,
      }),
    );
  }, [
    dispatch,
    selectedBranchId,
    selectedYearId,
    selectedTermId,
    selectedGradeId,
    selectedVoteHeadId,
  ]);

  useEffect(() => {
    fetchFeeStructuresList();
  }, [fetchFeeStructuresList]);

  // Pure Client-side Search over the loaded list
  const filteredFeeStructures = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return feeStructures;

    return feeStructures.filter((fs) => {
      const matchesVoteHead = fs.voteHeadName?.toLowerCase().includes(query);
      const matchesGrade = fs.gradeLevelName?.toLowerCase().includes(query);
      const matchesTerm = fs.termName?.toLowerCase().includes(query);
      const matchesYear = fs.academicYearName?.toLowerCase().includes(query);
      const matchesAmount = fs.amount?.toString().includes(query);

      return Boolean(
        matchesVoteHead ||
        matchesGrade ||
        matchesTerm ||
        matchesYear ||
        matchesAmount,
      );
    });
  }, [feeStructures, searchTerm]);

  // Modal Handlers
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingFeeStructureCreate) setIsCreateModalOpen(false);
  }, [loadingFeeStructureCreate]);

  const handleOpenUpdate = (feeStructure: FeeStructureResponseDto): void => {
    setSelectedFeeStructure(feeStructure);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingFeeStructureUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedFeeStructure(null);
    }
  }, [loadingFeeStructureUpdate]);

  const handleOpenDelete = (feeStructure: FeeStructureResponseDto): void => {
    setSelectedFeeStructure(feeStructure);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingFeeStructureDelete) {
      setIsDeleteModalOpen(false);
      setSelectedFeeStructure(null);
    }
  }, [loadingFeeStructureDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedFeeStructure) {
      dispatch(deleteFeeStructure(selectedFeeStructure.id));
    }
  };

  // Auto-close on success
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetFeeStructureServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Fee Structures"
      subtitle="Manage fee allocations across academic years, terms, and grade levels."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Fee Allocation
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by vote head, grade, term, or amount..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={setSelectedBranchId}
        />

        <AcademicYearSelectFilter
          value={selectedYearId}
          onChange={setSelectedYearId}
        />
        <TermSelectFilter value={selectedTermId} onChange={setSelectedTermId} />
        <VoteHeadSelectFilter
          value={selectedVoteHeadId}
          onChange={setSelectedVoteHeadId}
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingFeeStructures}
        error={errorLoadingFeeStructures}
        itemCount={filteredFeeStructures.length}
        emptyMessage="No Fee Structures match your criteria"
        onRetry={fetchFeeStructuresList}
      >
        {filteredFeeStructures.map((fs) => (
          <FeeStructureCard
            key={fs.id}
            feeStructure={fs}
            onEdit={canUpdate ? () => handleOpenUpdate(fs) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(fs) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateFeeStructureModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          userInfo={userInfo}
          loading={loadingFeeStructureCreate}
          error={errorCreatingFeeStructure}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedFeeStructure && (
        <UpdateFeeStructureModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          feeStructure={selectedFeeStructure}
          userInfo={userInfo}
          loading={loadingFeeStructureUpdate}
          error={errorUpdatingFeeStructure}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedFeeStructure && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Fee Allocation"
          description={`Are you sure you want to delete the ${selectedFeeStructure.voteHeadName} allocation for ${selectedFeeStructure.gradeLevelName}?`}
          loading={loadingFeeStructureDelete}
        />
      )}
    </PageContainer>
  );
};

export default FeeStructuresPage;
